import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import type { AdverseScreeningQuestionnaire } from '@app/src/pages/Findings/findingsUtils'
import type { BenchmarkGroupType } from '@app/src/types/benchmark'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import { useMemo } from 'react'
import type { BenchmarkData, ProfileBenchmarkResponse, ScreeningBenchmark, TopicBenchmark } from './types'

interface UseBenchmarkResult {
  benchmark?: BenchmarkData
  isLoading: boolean
}

export function toBenchmarkData(response: ProfileBenchmarkResponse): BenchmarkData {
  const topics: Record<number, TopicBenchmark> = {}
  for (const topic of response.topics ?? []) {
    topics[topic.topicId] = {
      mean: topic.mean,
      ownOpsMean: topic.ownOpsMean,
      supplyChainMean: topic.supplyChainMean,
    }
  }

  const screenings: Partial<Record<AdverseScreeningQuestionnaire, ScreeningBenchmark>> = {}
  for (const screening of response.screenings ?? []) {
    if (screening.screeningType) screenings[screening.screeningType] = { mean: screening.mean }
  }

  return {
    overall: response.overall,
    integrity: response.integrity,
    topics,
    screenings,
    baseline: response.baseline
      ? {
          overallMeanLevel: response.baseline.overallMeanLevel,
          levels: response.baseline.levels,
          areaMeanLevels: Object.fromEntries(
            (response.baseline.areaMeanLevels ?? []).map(({ area, meanLevel }) => [area, meanLevel]),
          ),
          peerCount: response.baseline.peerCount ?? 0,
          distribution: response.baseline.distribution ?? [],
        }
      : undefined,
  }
}

export function useBenchmark(providerId: number, groupType: BenchmarkGroupType): UseBenchmarkResult {
  const { data, isLoading } = useFetchResource<ProfileBenchmarkResponse | null>({
    key: [FetchKey.SustainabilityProfileBenchmark, providerId, groupType],
    endpoint: endpoints.sustainabilityProfileBenchmark(providerId, groupType),
    options: { enabled: Boolean(providerId), staleTime: ONE_MINUTE_MS * 10 },
    showSnackbarOn404: false,
  })

  const benchmark = useMemo(() => (data ? toBenchmarkData(data) : undefined), [data])

  return { benchmark, isLoading }
}
